import type { ApiErrorBody } from '../../shared/api.ts';

/**
 * The one place the client talks to the server.
 *
 * `T` is the caller's claim about the response body, not a promise this module
 * can keep — nothing here parses or validates. It defaults to `unknown`
 * DELIBERATELY: an unparameterised `api.get(...)` then produces an error at the
 * point of use, which is the whole reason the shapes in src/shared/api.ts
 * exist. Reach for a named type from there rather than an inline literal, so
 * the two sides of the wire keep pointing at one declaration.
 *
 * Two honest caveats about `Promise<T>`:
 *   - a 204 resolves `null`, and
 *   - a non-JSON response resolves the raw body text.
 * Both are cast, because encoding them in the return type would put a
 * `T | null | string` union on every call site to describe cases each one
 * already knows it isn't in. If a route can genuinely answer either way, say so
 * in ITS type (`T = Foo | null`).
 */

/** Thrown for every non-2xx. Carries the fields callers already read off it;
 *  runtime-identical to the plain Error this used to construct. */
export class ApiError extends Error {
  readonly status: number;
  /** 'preview' | 'stale' — lets callers branch without string-matching the
   *  message (see studio/CLAUDE.md: any new 409 needs one). Absent when the
   *  server didn't send one. */
  readonly code?: ApiErrorBody['code'];

  constructor(message: string, status: number, code?: ApiErrorBody['code']) {
    super(message);
    this.status = status;
    if (code) this.code = code;
  }
}

// Moved to src/shared so the server's 122 catch blocks share one helper rather
// than re-deriving it. Re-exported here, so no client importer moved.
export { errorMessage } from '../../shared/errors.ts';

/** The 409 discriminator, when the thrown value is one of ours. Callers branch
 *  on this rather than string-matching the message (see studio/CLAUDE.md). */
export function errorCode(err: unknown): ApiErrorBody['code'] | null {
  return err instanceof ApiError ? (err.code ?? null) : null;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** `signal` — normally `AbortSignal.timeout(ms)`. Polling callers need it: a
 *  fetch with no timeout can hang indefinitely, and a poll loop whose stall
 *  budgets are all evaluated after the response never reaches any of them.
 *  Without a bounded request those budgets are decoration.
 *
 *  `headers` — extra request headers, merged over the two this sets itself.
 *  Today only the staging acknowledgement (`lib/stagingGuard.ts`). */
export type RequestOpts = { signal?: AbortSignal; headers?: Record<string, string> };

async function request<T>(method: Method, path: string, body?: unknown, opts?: RequestOpts): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (opts?.signal) init.signal = opts.signal;
  if (body instanceof FormData) {
    init.body = body; // no Content-Type — the browser sets the multipart boundary
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  // Last, so a caller can override Accept or Content-Type deliberately rather
  // than having its header silently dropped for one it did not set.
  Object.assign(headers, opts?.headers);
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    let code: ApiErrorBody['code'];
    try {
      const data = (await res.json()) as Partial<ApiErrorBody> | null;
      if (data?.error) message = data.error;
      if (data?.code) code = data.code;
    } catch {}
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return null as T;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as T;
}

/**
 * What an author sees when a request never reached the server, or came back as
 * something other than JSON — a dev proxy's ECONNREFUSED page, a gateway error,
 * an empty body. Naming the exception instead is what surfaced "Failed to
 * execute 'json' on 'Response': Unexpected end of JSON input" to a user whose
 * actual problem was a studio that wasn't running.
 */
export const SERVER_UNREACHABLE = 'The studio server isn’t responding. Check it’s running, then try again.';

/**
 * Read a response body as JSON, or null when there isn't one. Never throws.
 *
 * For the handful of call sites that can't use `request()` because they branch
 * on a NON-OK body — the build flow reads `busy` and `reason` off a 4xx, which
 * `request()` collapses into an Error message — this is the piece of it they
 * still need. Everything else should use `api.*`.
 *
 * `T` is the caller's claim, on the same terms as `request()`: nothing here
 * validates. The `| null` is real and must be handled.
 */
export async function readJson<T = unknown>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOpts) => request<T>('GET', path, undefined, opts),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) => request<T>('POST', path, body, opts),
  /** Multipart upload — pass a FormData; error handling matches post(). */
  postForm: <T = unknown>(path: string, form: FormData) => request<T>('POST', path, form),
  put: <T = unknown>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  /** A body is optional and rare: only DELETE /api/people/:id takes one, to say
   *  where the person's decks should go. */
  delete: <T = unknown>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
